import { useCallback, useEffect, useRef } from "react";

/**
 * Map an id to a stable, zero-argument callback bound to that id.
 *
 * The returned closures keep their identity across renders, so the memoized
 * server/tool cards they're handed to don't re-render every time the picker
 * re-renders (a keystroke or selection change). They invoke the latest `fn`
 * through a ref, so the handler stays correct even as `fn` closes over changing
 * state such as the current selection.
 *
 * Handlers are built lazily, on first request, so a virtualized catalog of
 * thousands only allocates closures for the cards actually rendered. Passing
 * `ids` (the current working set) evicts closures for ids that are gone, keeping
 * the cache bounded as the catalog changes.
 */
export function useIdCallback(
  fn: (id: string) => void,
  ids: readonly string[]
): (id: string) => () => void {
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const cacheRef = useRef(new Map<string, () => void>());

  useEffect(() => {
    const live = new Set(ids);
    for (const id of cacheRef.current.keys()) {
      if (!live.has(id)) {
        cacheRef.current.delete(id);
      }
    }
  }, [ids]);

  return useCallback((id: string) => {
    const cache = cacheRef.current;
    const existing = cache.get(id);
    if (existing) {
      return existing;
    }
    const handler = () => fnRef.current(id);
    cache.set(id, handler);
    return handler;
  }, []);
}
